/**
 * Central object used for Store Auth module
 */

import Cookies from 'js-cookie'
import i18n from '@/plugins/i18n'
import Authentication from '@/api/MyLogin/Authentication'
import Student from '@/api/Dara/Student'
import { useAppStore } from '@/stores/app'
import { useStudentStore } from '@/stores/student'
import type IOauthTokens from '@/models/Authentication/OauthTokens'

// Token max age: 7 minutes (before the 8-minute auto-refresh)
// This gives us a 30-second buffer
const TOKEN_MAX_AGE_MS = 420000

function redirectToLogin() {
  window.sessionStorage.setItem('requested_page', window.location.pathname + window.location.search + window.location.hash)
  window.location.href = '/auth/login'
}

interface AuthState {
  access_token_timeout: ReturnType<typeof setTimeout> | undefined
  access_token: string | null
  access_token_last_refresh: number | null
  user_id: number | null
  student_id: number | null
  authenticated: boolean
  authorised: boolean
  is_shadowing: boolean
  has_bounced_email: boolean
  shouldClearQueryCache: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    access_token_timeout: undefined,
    access_token: localStorage.getItem('token'),
    access_token_last_refresh: localStorage.getItem('token') ? Date.now() : null,
    user_id: null,
    student_id: null,
    authenticated: false,
    authorised: false,
    is_shadowing: false,
    has_bounced_email: false,
    shouldClearQueryCache: false
  }),

  getters: {
    // Use a getters to access the Stores
    appStore(): ReturnType<typeof useAppStore> {
      return useAppStore()
    },

    hasRefreshToken() {
      const token = Cookies.get('x-refresh-token')

      return (!!token)
    },

    hasValidAccessToken(): boolean {
      // Check if we have an access token
      if (!this.access_token) return false

      const lastRefresh = this.access_token_last_refresh
      if (!lastRefresh) return false

      return Date.now() - lastRefresh < TOKEN_MAX_AGE_MS
    },

    accessToken(): string | null {
      return this.access_token
    }
  },

  actions: {
    getLoggedInUser() {
      let shadowedStudentId = +(window.localStorage.getItem('shadowed_student_id') || 0);
      if (shadowedStudentId) {
        return Authentication.getLoggedInMyFJUserShadow(shadowedStudentId)
          .then(({ data }) => {
            this.is_shadowing = true
            this.completeLogin(data)
          })
          .catch(error => {
            window.localStorage.removeItem('shadowed_student_id');
            // Re-throw so the promise chain knows it failed and prevents navigation
            throw error
          })
      }

      // Get the logged-in user
      return Authentication.getLoggedInMyFJUser()
        .then(({ data }) => {
          this.completeLogin(data)
        })
    },

    completeLogin({ id, locale, student_id, has_bounced_email, all_students }: App.Http.Resources.Login.UserMyFJV2Resource) {
      this.user_id = id
      this.student_id = student_id
      this.authenticated = true
      this.authorised = !!(id && student_id)
      this.has_bounced_email = has_bounced_email

      const locales = ['bg', 'cy', 'de_CH', 'de', 'en', 'es', 'et', 'fr', 'ga', 'gu', 'hi', 'hu', 'lt', 'lv', 'nl', 'pl', 'ro', 'sk', 'tr', 'uk', 'zh_CN', 'zh_TW'] as const
      type Locale = typeof locales[number]
      const userLocale: Locale = locales.includes(locale as Locale) ? locale as Locale : 'en'

      this.appStore.locale = userLocale
      document.documentElement.lang = userLocale

      if (userLocale !== localStorage.getItem('locale')) {
        this.shouldClearQueryCache = true
      }

      localStorage.setItem('locale', userLocale)
      i18n.global.locale.value = userLocale

      if (student_id) {
        useStudentStore().setAllStudentsData(all_students)

        Student.getStudent(student_id)
          .then(({ data }) => {
            useStudentStore().setStudentData(data)
          })
      }
    },

    processTokens(data: IOauthTokens) {
      const { access_token, refresh_token } = data

      if (!access_token || !refresh_token) {
        // Invalid tokens - throw error so calling code can handle it
        throw new Error('Invalid tokens received')
      }

      // Save the refresh token in a cookie
      Cookies.set('x-refresh-token', refresh_token || '')

      // Clear any existing timeout before setting a new one to prevent multiple timers
      if (this.access_token_timeout) {
        clearTimeout(this.access_token_timeout)
      }

      // Add the access token in local storage and reactive state
      localStorage.setItem('token', access_token || '')
      this.access_token = access_token || null
      this.access_token_last_refresh = Date.now()

      // Restart the 8-minute timer for next access token refresh 480000
      this.access_token_timeout = setTimeout(() => {
        void this.refreshToken().catch(() => {
          // Missing/invalid refresh cookie: refreshToken redirects to login or rethrows
        })
      }, 480000)
    },

    logout() {
      this.removeRefreshToken()

      // Remove access token in local storage and reactive state
      window.localStorage.removeItem('token')
      this.access_token = null
      this.access_token_last_refresh = null

      // Stop the auto access_token refresh
      clearTimeout(this.access_token_timeout)

      // Reset Auth data in Vuex
      this.resetAuth()
    },

    async refreshToken() {
      const token = Cookies.get('x-refresh-token')

      if (!token) {
        // Session cookie gone — redirect; use a stable message so Sentry can ignore it
        redirectToLogin()
        throw new Error('AUTH_SILENT_LOGIN_REDIRECT')
      }

      const params = {
        client_id: this.appStore.config.AUTH_PUBLIC_CLIENT_ID || '',
        grant_type: 'refresh_token',
        refresh_token: token,
      }

      // Attempt to get an access_token from refresh token silently
      // This should only be called by the axios interceptor on 401 errors
      try {
        const data = await Authentication.refreshToken(params)

        // Only remove old refresh token after successful refresh
        this.removeRefreshToken()
        this.processTokens(data)
      }
      catch (error) {
        // If refresh fails, remove the invalid refresh token
        // But don't redirect here - let the calling code handle it
        this.removeRefreshToken()
        throw error
      }
    },

    removeRefreshToken() {
      Cookies.remove('x-refresh-token')
    },

    async ensureFreshToken() {
      if (!this.hasValidAccessToken) {
        if (!this.hasRefreshToken) {
          redirectToLogin()
          throw new Error('AUTH_SILENT_LOGIN_REDIRECT')
        }

        await this.refreshToken()
      }
    },

    resetAuth() {
      this.user_id = null
      this.student_id = null
      this.authenticated = false
      this.authorised = false
      this.is_shadowing = false
      this.access_token_last_refresh = null
    },
  },
})
